import axios from "axios";

const getApiBaseUrl = () => {
  if (import.meta.env.MODE === 'production') {
    return import.meta.env.VITE_API_URL || 'https://ams-admin-v2-server.amajormusic.co.uk';
  }
  return 'http://localhost:3000';
};

const API_BASE_URL = getApiBaseUrl();

export const BILL_API = `${API_BASE_URL}/bills/`;
export const BILL_ADMIN_API = `${API_BASE_URL}/api/admin/bills/`;

export interface Bill {
  id: number;
  guid: string;
  xeroInvoiceId: string | null;
  billReference: string;
  purchaseOrderNumbers: string[]; // Array of PO numbers (e.g., ["PO001", "PO002"])
  supplierName?: string; // Supplier name (derived from PO relationships)
  receiptGuids: string[];
  totalAmount: number;
  createdDate: string;
  status: string;
  // Note: Bills can be DRAFT, NOT_FOUND_IN_XERO, UNKNOWN, etc.

  // Sync status tracking fields
  syncStatus: string; // SYNCED, PENDING, FAILED
  lastSyncError?: string | null;
  syncRetryCount: number;
  lastSyncAttempt?: string | null;

  // Flag for bills not found in Xero (prevents false 404 deletions)
  _missingInXero?: boolean;
}

export interface BillListResponse {
  bills: Bill[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Get all bills with optional filtering and pagination
 * Note: Only DRAFT bills are tracked in AMS (auto-cleaned on read)
 */
export const getBills = async (
  options?: {
    poNumber?: string;
    supplierGuid?: string;
    page?: number;
    limit?: number;
  }
): Promise<BillListResponse> => {
  const params: any = {};

  if (options?.poNumber) params.poNumber = options.poNumber;
  if (options?.supplierGuid) params.supplierGuid = options.supplierGuid;
  if (options?.page) params.page = options.page;
  if (options?.limit) params.limit = options.limit;

  const response = await axios.get(BILL_API, { params });
  return response.data;
};

/**
 * Get all bills for a specific purchase order
 */
export const getBillsByPurchaseOrder = async (
  poGuid: string
): Promise<Bill[]> => {
  const response = await axios.get(
    `${BILL_API}by-purchase-order/${poGuid}`
  );
  return response.data;
};

/**
 * Get a single bill by GUID
 */
export const getBill = async (guid: string): Promise<Bill> => {
  const response = await axios.get(`${BILL_API}${guid}`);
  return response.data;
};

/**
 * Merge multiple bills into one consolidated bill
 * Note: All bills in AMS are DRAFT
 */
export const mergeBills = async (billGuids: string[]): Promise<Bill> => {
  const response = await axios.post(`${BILL_API}merge`, {
    billGuids,
  });
  return response.data;
};

/**
 * Retry failed bill sync to Xero
 */
export const retryBillSync = async (billGuid: string): Promise<Bill> => {
  const response = await axios.post(`${BILL_API}${billGuid}/retry`);
  return response.data;
};

/**
 * Delete a bill from AMS and Xero
 */
export const deleteBill = async (billGuid: string): Promise<void> => {
  await axios.delete(`${BILL_API}${billGuid}`);
};

/**
 * Manually create a bill from specific receipts
 * Used when automatic bill creation fails or user wants to manually trigger bill creation
 * Bills created manually are marked with 'M' suffix in reference
 */
export const createBillFromReceipts = async (
  receiptGuids: string[],
  poGuid: string
): Promise<Bill> => {
  const response = await axios.post(`${BILL_API}create-from-receipts`, {
    receiptGuids,
    poGuid,
  });
  return response.data;
};

// ============================================================================
// Admin API Methods
// ============================================================================

export interface DiagnosticsSummary {
  total: number;
  synced: number;
  pending: number;
  failed: number;
  syncRate: string;
}

export interface FailedBill {
  guid: string;
  billReference: string;
  lastSyncError: string | null;
  syncRetryCount: number;
  lastSyncAttempt: string | null;
}

export interface DiagnosticsResponse {
  summary: DiagnosticsSummary;
  recentFailures: FailedBill[];
}

export interface Cleanup404Response {
  checked: number;
  deleted: number;
  errors: Array<{ billRef: string; error: string }>;
  deletedBills: string[];
  dryRun: boolean;
}

export interface BulkRetryResponse {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  errors: Array<{ billRef: string; error: string }>;
  dryRun: boolean;
}

export interface SyncStatusResponse {
  total: number;
  updated: number;
  unchanged: number;
  errors: Array<{ billRef: string; error: string }>;
}

/**
 * Get diagnostics summary for bill sync status
 * Returns counts and recent failures
 */
export const getDiagnostics = async (): Promise<DiagnosticsResponse> => {
  const response = await axios.get(`${BILL_ADMIN_API}diagnostics`);
  return response.data;
};

/**
 * Cleanup bills that return 404 from Xero
 * @param dryRun - If true, preview what would be deleted without actually deleting
 */
export const cleanup404Bills = async (dryRun: boolean = false): Promise<Cleanup404Response> => {
  const response = await axios.post(`${BILL_ADMIN_API}cleanup-404`, null, {
    params: { dryRun: dryRun.toString() }
  });
  return response.data;
};

/**
 * Sync all PENDING and FAILED bills to Xero
 * - PENDING bills: First sync attempt
 * - FAILED bills: Retry if under maxRetries limit
 * @param maxRetries - Maximum number of retries allowed for FAILED bills (default: 3)
 * @param dryRun - If true, preview what would be synced without actually syncing
 */
export const bulkRetryFailed = async (
  maxRetries: number = 3,
  dryRun: boolean = false
): Promise<BulkRetryResponse> => {
  const response = await axios.post(`${BILL_ADMIN_API}bulk-retry`, null, {
    params: {
      maxRetries: maxRetries.toString(),
      dryRun: dryRun.toString()
    }
  });
  return response.data;
};

/**
 * Sync all bill statuses with Xero
 * Updates sync status fields in database
 */
export const syncAllStatus = async (): Promise<SyncStatusResponse> => {
  const response = await axios.post(`${BILL_ADMIN_API}sync-status`);
  return response.data;
};

export interface SeedTestDataResponse {
  created: number;
  bills: Array<{
    billReference: string;
    guid: string;
    poNumber: string;
    supplierName: string;
    totalAmount: number;
    receiptCount: number;
    syncStatus: string;
  }>;
  message?: string;
}

/**
 * Seed test bill data from real unbilled receipts (development only)
 * Creates PENDING bills from actual receipt data with real suppliers and PO numbers
 */
export const seedTestData = async (): Promise<SeedTestDataResponse> => {
  const response = await axios.post(`${BILL_ADMIN_API}seed-test-data`);
  return response.data;
};
